import { useMemo, useState } from 'react';
import { Icon, StatusDot, type IconName } from '../chrome/Icon';
import {
  DashboardCalendar,
  dayActionCount,
  type CalendarSelection,
  type DayEvents,
} from '../activity/DashboardCalendar';
import { useActivities, useBabyMilestones, useImageCount } from '../../data/queries';
import { classifyMilestonePerformance, todayIso, uiStatus, type MilestonePerformance } from '../../data/derive';
import { longDate } from '../shared/rng';
import type { ActivityCategory, BabyMilestoneRow } from '../../data/dto';
import type { Baby } from '../../data/types';
import { useT, type TFn } from '../../i18n';

interface Props {
  baby: Baby;
  onClose: () => void;
  /** Open a milestone detail screen by numeric id. */
  onOpenDetail?: (id: string) => void;
  /** Legacy ignored. */
  accent?: string;
  accentInk?: string;
}

/** Maps raw category keys (snake_case + DB-capitalized) to a localized display label. */
function categoryLabels(t: TFn): Record<string, string> {
  const motorSkills = t('dashboard.category.motorSkills', 'Motor Skills');
  const communication = t('milestones.group.Communication', 'Communication');
  const socialEmotional = t('milestones.group.Social & Emotional', 'Social & Emotional');
  const cognitive = t('milestones.group.Cognitive', 'Cognitive');
  const boundaryStones = t('dashboard.category.boundaryStones', 'Boundary Stones');
  const language = t('milestones.group.Language', 'Language');
  const nutrition = t('milestones.group.Nutrition', 'Nutrition');
  const selfCare = t('milestones.group.Self-Care', 'Self-Care');
  const sleep = t('milestones.group.Sleep', 'Sleep');
  const teeth = t('milestones.group.Teeth', 'Teeth');
  return {
    // Existing snake_case keys (for safety)
    motor_gross: motorSkills,
    motor_fine: motorSkills,
    communication,
    social_emotional: socialEmotional,
    cognitive,

    // DB capitalized keys
    'Motor - Gross': motorSkills,
    'Motor - Fine': motorSkills,
    'Communication': communication,
    'Social & Emotional': socialEmotional,
    'Cognitive': cognitive,
    'Boundary Stone': boundaryStones,
    'Language': language,
    'Nutrition': nutrition,
    'Self-Care': selfCare,
    'Sleep': sleep,
    'Teeth': teeth,
  };
}

function activityCategories(t: TFn): { key: ActivityCategory; label: string; icon: IconName }[] {
  return [
    { key: 'play', label: t('dashboard.activity.play', 'Play'), icon: 'play' },
    { key: 'sing', label: t('dashboard.activity.sing', 'Sing'), icon: 'sing' },
    { key: 'music', label: t('dashboard.activity.music', 'Music'), icon: 'music' },
    { key: 'read', label: t('dashboard.activity.read', 'Read'), icon: 'read' },
    { key: 'excursion', label: t('dashboard.activity.excursion', 'Excursion'), icon: 'explore' },
  ];
}

/** Visual treatment for each performance verdict — independent of the gender accent. */
function perfMeta(t: TFn): Record<MilestonePerformance, { label: string; ink: string; bg: string }> {
  return {
    ahead: { label: t('dashboard.perf.ahead', 'Ahead'), ink: '#3F8F6B', bg: 'rgba(63, 143, 107, 0.14)' },
    median: { label: t('dashboard.perf.median', 'In the median'), ink: 'var(--color-ink-2)', bg: 'var(--color-grey-soft)' },
    late: { label: t('dashboard.perf.late', 'Late'), ink: 'var(--color-amber-ink)', bg: 'var(--color-amber-soft)' },
    at_risk: { label: t('dashboard.perf.atRisk', 'At risk'), ink: '#B0432C', bg: 'rgba(176, 67, 44, 0.14)' },
  };
}

function PerfBadge({ perf }: { perf: MilestonePerformance }) {
  const t = useT();
  const p = perfMeta(t)[perf];
  return (
    <span
      className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
      style={{ color: p.ink, background: p.bg }}
    >
      {p.label}
    </span>
  );
}

/**
 * Dashboard drill-in from the Today screen's "Progress at a glance" card.
 * Renders as a full screen, not a sheet — has its own back button.
 */
export function Dashboard({ baby, onClose, onOpenDetail }: Props) {
  const t = useT();
  const CATEGORY_LABELS = useMemo(() => categoryLabels(t), [t]);
  const ACTIVITY_CATEGORIES = useMemo(() => activityCategories(t), [t]);
  const msQ       = useBabyMilestones(baby.id ?? null);
  const actsQ     = useActivities(baby.id ?? null);
  const imagesQ   = useImageCount(baby.id ?? null);

  const milestones = useMemo(() => msQ.data?.milestones ?? [], [msQ.data]);
  const activities = actsQ.data ?? [];

  const counts = msQ.data?.counts ?? { achieved: 0, in_progress: 0, not_started: 0, total: 0 };
  const done   = counts.achieved;
  const now    = counts.in_progress;
  const soon   = counts.not_started;
  const total  = counts.total || 1;

  const dobIso = baby.dobIso;
  const today  = todayIso();

  // Parse DOB once for the calendar.
  const dob = useMemo(() => {
    const iso = baby.dobIso ?? baby.dob;
    try {
      const d = new Date(iso.length === 10 ? iso + 'T00:00:00' : iso);
      return Number.isNaN(d.getTime()) ? undefined : d;
    } catch {
      return undefined;
    }
  }, [baby.dobIso, baby.dob]);

  /* ── Per-day events: activities done + milestones started / finished ── */
  const events = useMemo(() => {
    const map = new Map<string, DayEvents>();
    const ensure = (k: string): DayEvents => {
      let e = map.get(k);
      if (!e) { e = { activities: [], started: [], finished: [] }; map.set(k, e); }
      return e;
    };
    for (const m of milestones) {
      if (m.started_at) ensure(m.started_at.slice(0, 10)).started.push({ id: m.milestone_id, name: m.name });
      if (m.status === 'achieved' && m.achieved_date) {
        ensure(m.achieved_date.slice(0, 10)).finished.push({ id: m.milestone_id, name: m.name });
      }
    }
    for (const a of activities) {
      if (a.state === 'done' && a.done_date) {
        ensure(a.done_date.slice(0, 10)).activities.push({ id: a.id, title: a.title, category: a.category });
      }
    }
    return map;
  }, [milestones, activities]);

  const [selection, setSelection] = useState<CalendarSelection>(() => {
    const d = new Date(today + 'T00:00:00');
    return { kind: 'day', key: today, date: d, days: [today] };
  });

  // Merge the selected day's (or week's) events into one breakdown.
  const selEvents = useMemo<DayEvents>(() => {
    const merged: DayEvents = { activities: [], started: [], finished: [] };
    for (const k of selection.days) {
      const e = events.get(k);
      if (!e) continue;
      merged.activities.push(...e.activities);
      merged.started.push(...e.started);
      merged.finished.push(...e.finished);
    }
    return merged;
  }, [selection, events]);

  const selHeading =
    selection.kind === 'day'
      ? longDate(selection.date)
      : `${rangeLabel(selection.start)} – ${rangeLabel(selection.end)}`;

  /* ── Milestones grouped by display category, with performance ── */
  const byCategory = useMemo(() => {
    const groups = new Map<string, { items: { m: BabyMilestoneRow; perf: ReturnType<typeof classifyMilestonePerformance> }[]; done: number }>();
    for (const m of milestones) {
      const cat = CATEGORY_LABELS[m.group_key] ?? m.group_key;
      const g = groups.get(cat) ?? { items: [], done: 0 };
      g.items.push({ m, perf: classifyMilestonePerformance(m, dobIso, today) });
      if (uiStatus(m.status) === 'done') g.done += 1;
      groups.set(cat, g);
    }
    return Array.from(groups, ([cat, g]) => {
      let worstPerf: MilestonePerformance | null = null;
      for (const item of g.items) {
        if (!item.perf) continue;
        const p = item.perf.perf;
        if (worstPerf === null) {
          worstPerf = p;
        } else if (p === 'at_risk') {
          worstPerf = 'at_risk';
        } else if (p === 'late' && worstPerf !== 'at_risk') {
          worstPerf = 'late';
        } else if (p === 'median' && worstPerf !== 'at_risk' && worstPerf !== 'late') {
          worstPerf = 'median';
        }
      }
      return { cat, items: g.items, total: g.items.length, done: g.done, summary: worstPerf };
    });
  }, [milestones, dobIso, today, CATEGORY_LABELS]);

  const [openCat, setOpenCat] = useState<string | null>(null);

  /* ── Activities done per category ── */
  const activityCounts = useMemo(() => {
    const c: Record<string, number> = { play: 0, sing: 0, read: 0, excursion: 0 };
    for (const a of activities) if (a.state === 'done') c[a.category] = (c[a.category] ?? 0) + 1;
    return c;
  }, [activities]);
  const totalActivitiesDone = ACTIVITY_CATEGORIES.reduce((s, c) => s + (activityCounts[c.key] ?? 0), 0);

  return (
    <div className="flex flex-col gap-5 pb-6">
      {/* Header with back */}
      <div className="flex items-center justify-between">
        <button
          onClick={onClose}
          aria-label={t('common.back', 'Back')}
          className="flex min-h-11 min-w-11 -ml-2.5 items-center text-ink"
        >
          <span className="-scale-x-100">
            <Icon name="chevron" size={22} color="var(--color-ink)" />
          </span>
        </button>
        <span className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
          {t('dashboard.title', 'Dashboard')}
        </span>
        <span className="min-w-11" />
      </div>

      {/* Title */}
      <div>
        <h1 className="font-serif text-3xl nav:text-4xl leading-tight tracking-tight text-ink whitespace-pre-line">
          {t('dashboard.heading', '{name}’s progress\nso far').replace('{name}', baby.name)}
        </h1>
        <p className="mt-2.5 max-w-[290px] text-[13.5px] text-ink-2">
          {t('dashboard.summary', '{done} milestones reached out of {total} tracked across the first 24 months.')
            .replace('{done}', String(done))
            .replace('{total}', String(total))}
        </p>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-2.5">
        {[
          { n: done, l: t('status.achieved', 'Achieved'), c: 'var(--color-accent)' },
          { n: now,  l: t('dashboard.stat.onTrack', 'On track'), c: 'var(--color-amber)'  },
          { n: soon, l: t('dashboard.stat.upcoming', 'Upcoming'), c: 'var(--color-grey)'   },
        ].map((s, i) => (
          <div key={i} className="rounded-xl border border-line bg-surface px-3.5 pt-3.5 pb-3">
            <div className="flex items-center gap-1.5">
              <span className="size-1.5 rounded-full" style={{ background: s.c }} />
              <span className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-3">
                {s.l}
              </span>
            </div>
            <div className="mt-1 font-serif text-[28px] leading-none tracking-tight text-ink">
              {s.n}
            </div>
          </div>
        ))}
      </div>

      {/* Activity calendar */}
      {dob && (
        <div>
          <DashboardCalendar dob={dob} events={events} selection={selection} onSelect={setSelection} />

          {/* Selected day / week breakdown */}
          <div className="mt-3 rounded-[14px] border border-hairline bg-bg px-3.5 py-3">
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-serif text-[15px] tracking-tight text-ink">
                {selection.kind === 'week' ? t('dashboard.weekOf', 'Week of ') : ''}{selHeading}
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-3">
                {dayActionCount(selEvents)} {dayActionCount(selEvents) === 1 ? t('dashboard.action', 'action') : t('dashboard.actions', 'actions')}
              </span>
            </div>

            {dayActionCount(selEvents) === 0 ? (
              <p className="mt-2 text-[12.5px] leading-snug text-ink-2">
                {selection.kind === 'week' ? t('dashboard.quietWeek', 'A quiet week. Rest counts too.') : t('dashboard.quietDay', 'A quiet day. Rest counts too.')}
              </p>
            ) : (
              <div className="mt-2.5 flex flex-col gap-3">
                {selEvents.finished.length > 0 && (
                  <DaySection title={t('dashboard.milestonesWon', 'Milestones won')}>
                    {selEvents.finished.map((m) => (
                      <DayRow key={`f${m.id}`} onClick={() => onOpenDetail?.(String(m.id))}>
                        <Icon name="trophy" size={14} color="var(--color-amber-ink)" />
                        <span className="min-w-0 flex-1 truncate">{m.name}</span>
                      </DayRow>
                    ))}
                  </DaySection>
                )}
                {selEvents.started.length > 0 && (
                  <DaySection title={t('dashboard.milestonesStarted', 'Milestones started')}>
                    {selEvents.started.map((m) => (
                      <DayRow key={`s${m.id}`} onClick={() => onOpenDetail?.(String(m.id))}>
                        <span className="size-1.5 shrink-0 rounded-full bg-amber" />
                        <span className="min-w-0 flex-1 truncate">{m.name}</span>
                      </DayRow>
                    ))}
                  </DaySection>
                )}
                {selEvents.activities.length > 0 && (
                  <DaySection title={t('dashboard.activitiesDone', 'Activities done')}>
                    {selEvents.activities.map((a, i) => (
                      <DayRow key={`a${a.id}-${i}`}>
                        <span className="size-1.5 shrink-0 rounded-full bg-accent" />
                        <span className="min-w-0 flex-1 truncate">{a.title}</span>
                        <span className="shrink-0 text-[11px] text-ink-3">
                          {ACTIVITY_CATEGORIES.find((c) => c.key === a.category)?.label ?? a.category}
                        </span>
                      </DayRow>
                    ))}
                  </DaySection>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Per-category milestone breakdown */}
      <section>
        <h2 className="mb-3 font-serif text-xl tracking-tight text-ink">{t('dashboard.byCategory', 'By category')}</h2>
        <div className="overflow-hidden rounded-xl border border-line bg-surface">
          {byCategory.map(({ cat, items, total: catTotal, done: catDone, summary }, i, a) => {
            const pct = catTotal === 0 ? 0 : (catDone / catTotal) * 100;
            const isOpen = openCat === cat;
            return (
              <div key={cat} className={i < a.length - 1 ? 'border-b border-hairline' : ''}>
                <button
                  onClick={() => setOpenCat(isOpen ? null : cat)}
                  className="flex min-h-11 w-full items-center gap-2 px-4 py-3.5 text-left"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-ink">{cat}</span>
                      {summary && <PerfBadge perf={summary} />}
                    </div>
                    <div className="mt-2 h-[3px] overflow-hidden rounded-sm bg-grey-soft">
                      <div className="h-full bg-accent" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <span className="shrink-0 text-xs font-semibold text-ink-3 tabular-nums">
                    {catDone}/{catTotal}
                  </span>
                  <span className={isOpen ? 'rotate-180 transition-transform' : 'transition-transform'}>
                    <Icon name="down" size={16} color="var(--color-ink-3)" />
                  </span>
                </button>

                {isOpen && (
                  <ul className="bg-bg/40 px-4 pb-3">
                    {items.map(({ m, perf }) => (
                      <li key={m.milestone_id}>
                        <button
                          onClick={() => onOpenDetail?.(String(m.milestone_id))}
                          className="flex min-h-10 w-full items-center gap-2 border-t border-hairline py-2.5 text-left"
                        >
                          <StatusDot
                            status={uiStatus(m.status)}
                            color="var(--color-accent)"
                            size={18}
                          />
                          <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{m.name}</span>
                          {perf ? (
                            <PerfBadge perf={perf.perf} />
                          ) : (
                            <span className="text-[10px] uppercase tracking-wide text-ink-3">{t('dashboard.stat.upcoming', 'Upcoming')}</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
          {byCategory.length === 0 && (
            <div className="px-4 py-5 text-center text-[13px] italic text-ink-3">{t('common.loading', 'Loading…')}</div>
          )}
        </div>
      </section>

      {/* Activities done per category */}
      <section>
        <h2 className="mb-3 font-serif text-xl tracking-tight text-ink">
          {t('dashboard.activitiesDone', 'Activities done')}
          <span className="ml-2 align-middle text-[13px] font-normal text-ink-3">{totalActivitiesDone} {t('dashboard.total', 'total')}</span>
        </h2>
        <div className="grid grid-cols-2 gap-2.5">
          {ACTIVITY_CATEGORIES.map(({ key, label, icon }) => (
            <div key={key} className="flex items-center gap-3 rounded-xl border border-line bg-surface px-3.5 py-3">
              <span
                className="flex size-9 shrink-0 items-center justify-center rounded-full"
                style={{ background: 'color-mix(in oklch, var(--color-accent) 12%, var(--color-bg))' }}
              >
                <Icon name={icon} size={18} color="var(--color-accent-ink)" />
              </span>
              <div className="min-w-0">
                <div className="font-serif text-[22px] leading-none tracking-tight text-ink tabular-nums">
                  {activityCounts[key] ?? 0}
                </div>
                <div className="mt-0.5 text-[11.5px] text-ink-3">{label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Images */}
      <section>
        <h2 className="mb-3 font-serif text-xl tracking-tight text-ink">{t('dashboard.images', 'Images')}</h2>
        <div className="flex items-center gap-3.5 rounded-xl border border-line bg-surface px-4 py-3.5">
          <span
            className="flex size-11 shrink-0 items-center justify-center rounded-full"
            style={{ background: 'color-mix(in oklch, var(--color-accent) 12%, var(--color-bg))' }}
          >
            <Icon name="calendar" size={20} color="var(--color-accent-ink)" />
          </span>
          <div>
            <div className="font-serif text-[28px] leading-none tracking-tight text-ink tabular-nums">
              {imagesQ.data ?? 0}
            </div>
            <div className="mt-1 text-[12px] text-ink-3">
              {(imagesQ.data ?? 0) === 1 ? t('dashboard.photoCaptured', 'photo captured') : t('dashboard.photosCaptured', 'photos captured')}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/** "May 5" — short month + day, for week range labels. */
function rangeLabel(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function DaySection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-ink-3">{title}</div>
      <ul className="flex flex-col gap-1">{children}</ul>
    </div>
  );
}

function DayRow({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  const cls = 'flex w-full items-center gap-2.5 text-left text-[12.5px] leading-snug text-ink';
  if (onClick) {
    return (
      <li>
        <button onClick={onClick} className={cls + ' min-h-9'}>{children}</button>
      </li>
    );
  }
  return <li className={cls}>{children}</li>;
}
